import React, { forwardRef } from "react";
import { twMerge } from "tailwind-merge";
import Typography from "./typography";

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  helperText?: string;
  error?: string;
  fullWidth?: boolean;
  containerClassName?: string;
  labelClassName?: string;
  inputClassName?: string;
  helperTextClassName?: string;
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      helperText,
      error,
      fullWidth = false,
      className,
      containerClassName,
      labelClassName,
      inputClassName,
      helperTextClassName,
      disabled,
      ...props
    },
    ref
  ) => {
    const textareaClasses = twMerge(
      "border rounded-md outline-none transition-all duration-200 font-sans",
      "focus:ring-0 focus:ring-transparent focus:border-teal-500",
      "placeholder:text-text-light",
      "px-3 py-2 text-base",
      fullWidth ? "w-full" : "",
      disabled ? "text-gray-400 cursor-not-allowed" : "text-text",
      error
        ? "border-red-500 focus:ring-red-200 focus:border-red-500"
        : "border-teal-100 hover:border-teal-200",
      className,
      inputClassName
    );

    const containerClasses = twMerge(
      "flex flex-col",
      fullWidth ? "w-full" : "",
      containerClassName
    );

    const labelClasses = twMerge(
      "block font-medium text-emerald-700 mb-1",
      error ? "text-red-500" : "",
      labelClassName
    );

    const helperTextClasses = twMerge(
      "mt-1 text-sm",
      error ? "text-red-500" : "text-emerald-600",
      helperTextClassName
    );

    return (
      <div className={containerClasses}>
        {label ? (
          <label htmlFor={props.id} className={labelClasses}>
            <Typography variant="caption">{label}</Typography>
          </label>
        ) : null}

        <textarea
          ref={ref}
          className={textareaClasses}
          disabled={disabled}
          {...props}
        />

        {(helperText || error) && (
          <Typography variant="caption" className={helperTextClasses}>
            {error || helperText}
          </Typography>
        )}
      </div>
    );
  }
);

Textarea.displayName = "Textarea";

export default Textarea;
